// ============================================================================
// CrossOnboardingHost (W7-B, E11 / Karte C §Empfehlung e).
// ----------------------------------------------------------------------------
// „Wer nur Begleiter nutzt, wird einmal auf Abenteuer angesprochen — und
// umgekehrt." Aufbau nach `ReviewPromptHost.tsx`: kopflos im App-Baum, das
// TIMING gehoert dem Server.
//
//   GET  /api/auth/me            → cross_onboarding_offer: null | { target, character_name? }
//   POST /api/profile/cross-onboarding { answer: 'yes' | 'no' } → { state }
//
// Der Client zaehlt NICHTS selbst mit (E11 verlangt „2. Sitzung oder Tag 2,
// bei Nein nach einer Woche nochmal, dann nie" — das muss eine Neuinstallation
// ueberleben, siehe Karte C §7.2). Er bremst nur zusaetzlich:
//   • hoechstens EINE Frage je Browser-Sitzung (sessionStorage), damit
//     SPA-Navigation keine Serie ausloest;
//   • nie ueber einem Startup-Gate (Legal-Reakzeptanz, Profil-Abschluss,
//     Push-Frage) — dafuer liest er useStartupGate() === 'none'. Das gilt auch
//     WAEHREND der Dialog steht: schiebt sich ein Gate nachtraeglich davor,
//     raeumt er sich weg (W8-C/MINOR 8).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../api/client';
import type { CrossOnboardingOffer } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { Button, ResponsiveDialog } from '../components/ui';
import { useT } from '../i18n';
import { useStartupGate } from './StartupGateCoordinator';

/** Sitzungsbremse, Muster REVIEW_PROMPT_SESSION_KEY (reviewPrompt.ts:21). */
export const CROSS_ONBOARDING_SESSION_KEY = 'lorath_cross_onboarding_session';

/** Ziel-Routen der beiden Tueren (Karte C §Empfehlung e). */
const TARGET_ROUTES: Record<CrossOnboardingOffer['target'], string> = {
  companion: '/characters/new?mode=companion',
  adventure: '/campaign-new',
};

function readSessionStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function sessionAsked(session: Storage | null): boolean {
  if (!session) return false;
  try {
    return session.getItem(CROSS_ONBOARDING_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markSessionAsked(session: Storage | null): void {
  if (!session) return;
  try {
    session.setItem(CROSS_ONBOARDING_SESSION_KEY, '1');
  } catch {
    // Privatmodus: dann bremst nur noch der Serverzustand — und der ist die
    // eigentliche Wahrheit.
  }
}

/** Bremse zuruecknehmen — die Frage wurde nie gestellt, nur weggeraeumt. */
function clearSessionAsked(session: Storage | null): void {
  if (!session) return;
  try {
    session.removeItem(CROSS_ONBOARDING_SESSION_KEY);
  } catch {
    // s.o.
  }
}

/** Nur das erwartete Angebot durchlassen; alles andere gilt als „kein Angebot". */
export function normalizeCrossOnboardingOffer(value: unknown): CrossOnboardingOffer | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as { target?: unknown; character_name?: unknown };
  if (raw.target !== 'companion' && raw.target !== 'adventure') return null;
  const name = typeof raw.character_name === 'string' && raw.character_name.trim()
    ? raw.character_name.trim()
    : undefined;
  return name ? { target: raw.target, character_name: name } : { target: raw.target };
}

export function CrossOnboardingHost() {
  const { t } = useT();
  const navigate = useNavigate();
  const auth = useAuth();
  const startupGate = useStartupGate();
  const [offer, setOffer] = useState<CrossOnboardingOffer | null>(null);
  const [busy, setBusy] = useState(false);

  const serverOffer = normalizeCrossOnboardingOffer(
    auth.status === 'authed' && !auth.user?.newcomer_mode ? auth.user?.cross_onboarding_offer : null,
  );
  const serverTarget = serverOffer?.target ?? null;
  const serverName = serverOffer?.character_name ?? null;

  useEffect(() => {
    if (!serverTarget) return;
    // Nie ueber einem Gate: erst zeigen, wenn wirklich nichts anderes blockiert.
    if (startupGate !== 'none') return;
    const session = readSessionStorage();
    if (sessionAsked(session)) return;
    markSessionAsked(session);
    setOffer(serverName ? { target: serverTarget, character_name: serverName } : { target: serverTarget });
  }, [serverName, serverTarget, startupGate]);

  // W8-C (MINOR 8): `startupGate` wurde bisher NUR beim Oeffnen geprueft. Kam
  // danach ein Gate hoch (z.B. der Push-Rueckfall nach 5 Minuten), standen zwei
  // Dialoge uebereinander. Jetzt raeumt der Dialog sich selbst weg — OHNE eine
  // Antwort zu senden — und gibt die Sitzungsbremse wieder frei, damit das
  // Angebot beim naechsten /me erneut erscheinen kann.
  useEffect(() => {
    if (startupGate === 'none' && !auth.user?.newcomer_mode) return;
    if (!offer) return;
    clearSessionAsked(readSessionStorage());
    setOffer(null);
  }, [offer, startupGate, auth.user?.newcomer_mode]);

  const answer = useCallback(async (value: 'yes' | 'no') => {
    const current = offer;
    if (!current || busy) return;
    setBusy(true);
    // Der Dialog schliesst SOFORT — die Antwort ist eine Nebensache, die auch
    // offline nicht im Weg stehen darf. Bei „Ja" wird trotzdem navigiert; das
    // Angebot verschwindet spaetestens mit dem naechsten /me.
    setOffer(null);
    try {
      await apiRequest<{ state?: string }>('/api/profile/cross-onboarding', {
        method: 'POST',
        body: { answer: value },
      });
    } catch {
      // Netzfehler/401: still bleiben. Die Sitzungsbremse steht bereits, der
      // Server bietet es beim naechsten Mal erneut an.
    } finally {
      setBusy(false);
    }
    if (value === 'yes') navigate(TARGET_ROUTES[current.target]);
    // Nach der Antwort den Serverstand frisch holen, damit das Angebot nicht im
    // Auth-Cache stehen bleibt.
    await auth.refresh().catch(() => undefined);
  }, [auth, busy, navigate, offer]);

  if (!offer) return null;

  const isCompanion = offer.target === 'companion';
  const body = isCompanion
    ? t('cross_onboarding.companion_body')
    : offer.character_name
      ? t('cross_onboarding.adventure_body_named', { name: offer.character_name })
      : t('cross_onboarding.adventure_body');

  return (
    <ResponsiveDialog
      open
      layer="foreground"
      onClose={() => { void answer('no'); }}
      title={isCompanion
        ? t('cross_onboarding.companion_title')
        : t('cross_onboarding.adventure_title')}
      maxWidth={480}
      footer={(
        <>
          <Button variant="secondary" disabled={busy} onClick={() => { void answer('no'); }}>
            {t('cross_onboarding.decline')}
          </Button>
          <Button data-autofocus disabled={busy} onClick={() => { void answer('yes'); }}>
            {t('cross_onboarding.accept')}
          </Button>
        </>
      )}
    >
      <p>{body}</p>
    </ResponsiveDialog>
  );
}
